import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import { useTheme } from '@mui/material/styles';

const StatusBadge = ({
  status,
  label,
  showIcon = true,
  size = 'medium', // 'small' | 'medium' | 'large'
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const statusConfig = {
    active: {
      bg: isDark ? 'rgba(132, 204, 22, 0.15)' : '#f2fde0',
      color: '#65a30d',
      icon: CheckCircleIcon,
      label: 'Active',
    },
    inactive: {
      bg: isDark ? 'rgba(100, 116, 139, 0.15)' : '#f1f5f9',
      color: isDark ? '#94a3b8' : '#64748b',
      icon: CancelIcon,
      label: 'Inactive',
    },
    pending: {
      bg: isDark ? 'rgba(255, 174, 31, 0.15)' : '#fef5e5',
      color: '#ffae1f',
      icon: PendingIcon,
      label: 'Pending',
    },
    approved: {
      bg: isDark ? 'rgba(132, 204, 22, 0.15)' : '#f2fde0',
      color: '#65a30d',
      icon: CheckCircleIcon,
      label: 'Approved',
    },
    denied: {
      bg: isDark ? 'rgba(250, 137, 107, 0.15)' : '#fdede8',
      color: '#fa896b',
      icon: CancelIcon,
      label: 'Denied',
    },
    expired: {
      bg: isDark ? 'rgba(250, 137, 107, 0.15)' : '#fdede8',
      color: '#fa896b',
      icon: ErrorIcon,
      label: 'Expired',
    },
    disabled: {
      bg: isDark ? 'rgba(100, 116, 139, 0.15)' : '#f1f5f9',
      color: isDark ? '#94a3b8' : '#64748b',
      icon: CancelIcon,
      label: 'Disabled',
    },
    success: {
      bg: isDark ? 'rgba(132, 204, 22, 0.15)' : '#f2fde0',
      color: '#65a30d',
      icon: CheckCircleIcon,
      label: 'Success',
    },
    warning: {
      bg: isDark ? 'rgba(255, 174, 31, 0.15)' : '#fef5e5',
      color: '#ffae1f',
      icon: ErrorIcon,
      label: 'Warning',
    },
    error: {
      bg: isDark ? 'rgba(250, 137, 107, 0.15)' : '#fdede8',
      color: '#fa896b',
      icon: ErrorIcon,
      label: 'Error',
    },
    cancelled: {
      bg: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
      color: '#dc2626',
      icon: CancelIcon,
      label: 'Cancelled',
    },
  };

  const config = statusConfig[status?.toLowerCase()] || statusConfig.inactive;
  const displayLabel = label || config.label;
  const Icon = config.icon;

  const sizeStyles = {
    small: {
      px: 1,
      py: 0.25,
      fontSize: '0.6875rem',
      iconSize: 12,
    },
    medium: {
      px: 1.5,
      py: 0.5,
      fontSize: '0.75rem',
      iconSize: 14,
    },
    large: {
      px: 2,
      py: 0.75,
      fontSize: '0.8125rem',
      iconSize: 16,
    },
  };

  const sizes = sizeStyles[size] || sizeStyles.medium;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: sizes.px,
        py: sizes.py,
        borderRadius: '9999px',
        backgroundColor: 'transparent',
      }}
    >
      {showIcon && Icon && (
        <Icon sx={{ fontSize: sizes.iconSize, color: config.color }} />
      )}
      <Typography
        sx={{
          fontSize: sizes.fontSize,
          fontWeight: 600,
          color: config.color,
          textTransform: 'capitalize',
          lineHeight: 1.4,
        }}
      >
        {displayLabel}
      </Typography>
    </Box>
  );
};

export default StatusBadge;
